import type { Router } from 'vue-router'
import { getCookieConsent, onCookieConsentChange } from './cookieConsent'

declare global {
  interface Window {
    ym?: ((id: number | string, method: string, ...args: unknown[]) => void) & {
      a?: unknown[]
      l?: number
    }
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let isAnalyticsLoaded = false
let routerListenerInitialized = false

/**
 * Инициализация Яндекс Метрики (только если передан counter ID)
 */
function loadYandexMetrika(counterId: string | number): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (document.getElementById('ym-script')) return

  const numericId = typeof counterId === 'string' ? parseInt(counterId, 10) : counterId
  if (!numericId || Number.isNaN(numericId)) return

  window.ym =
    window.ym ||
    function (...args: unknown[]) {
      ;(window.ym!.a = window.ym!.a || []).push(args)
    }
  window.ym.l = Date.now()

  const script = document.createElement('script')
  script.id = 'ym-script'
  script.type = 'text/javascript'
  script.async = true
  script.src = 'https://mc.yandex.ru/metrika/tag.js'

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  window.ym(numericId, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
  })
}

/**
 * Инициализация Google Analytics (только если передан Measurement ID)
 */
function loadGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (document.getElementById('ga-script')) return
  if (!measurementId || !measurementId.trim()) return

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function (...args: unknown[]) {
      window.dataLayer!.push(args)
    }

  window.gtag('js', new Date())
  window.gtag('config', measurementId)

  const script = document.createElement('script')
  script.id = 'ga-script'
  script.type = 'text/javascript'
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`

  document.head.appendChild(script)
}

/**
 * Загрузка всех аналитических скриптов
 */
export function loadAnalytics(): void {
  if (isAnalyticsLoaded) return
  if (getCookieConsent() !== 'accepted') return

  const ymId = import.meta.env.VITE_YANDEX_METRIKA_ID
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID

  if (ymId) {
    loadYandexMetrika(ymId)
  }

  if (gaId) {
    loadGoogleAnalytics(gaId)
  }

  isAnalyticsLoaded = true
}

/**
 * Отправка события просмотра страницы в счетчики
 */
export function trackPageView(path: string): void {
  if (!isAnalyticsLoaded || getCookieConsent() !== 'accepted') return

  const ymId = import.meta.env.VITE_YANDEX_METRIKA_ID
  if (ymId && typeof window.ym === 'function') {
    const numericId = parseInt(ymId, 10)
    if (!Number.isNaN(numericId)) {
      window.ym(numericId, 'hit', path)
    }
  }

  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID
  if (gaId && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
    })
  }
}

/**
 * Инициализация менеджера аналитики.
 * Отслеживает текущее и будущее согласие пользователя и навигацию router.
 */
export function initAnalytics(router?: Router): void {
  // Если согласие уже дано — загружаем аналитику
  if (getCookieConsent() === 'accepted') {
    loadAnalytics()
  }

  // Подписываемся на изменение согласия
  onCookieConsentChange((status) => {
    if (status === 'accepted') {
      loadAnalytics()
    }
  })

  // Привязываемся к переходам vue-router для SPA hit
  if (router && !routerListenerInitialized) {
    routerListenerInitialized = true
    router.afterEach((to) => {
      trackPageView(to.fullPath)
    })
  }
}

/**
 * Сброс состояния для тестов
 */
export function _resetAnalyticsState(): void {
  isAnalyticsLoaded = false
  routerListenerInitialized = false
  document.getElementById('ym-script')?.remove()
  document.getElementById('ga-script')?.remove()
  delete window.ym
  delete window.gtag
  delete window.dataLayer
}
